import { call, put, takeLatest, type SagaReturnType } from '@spcc/core'
import type { PayloadAction } from '@spcc/core'
import { showSuccessToast, showErrorToast } from '@spcc/core'

import { authApi } from '../infrastructure/api'
import { authActions } from './auth.slice'
import {
  storeSession,
  clearAuthStorage,
  getStoredSession,
  hasValidToken,
  isPersistentSession,
} from '../domain/utils'
import type { 
  AuthUser,
  LoginCredentials, 
  RegisterData, 
  ForgotPasswordRequest, 
  ResetPasswordRequest,
  ChangePasswordRequest,
  ChangePasswordOtpRequest,
  DeleteAccountRequest,
  UpdateProfileRequest,
} from '../domain/types'
import { adminContextActions } from '@spcc/domains/admin-context'
import { initActions } from '@spcc/domains/portal-init'

function* initializeAuthSaga() {
  try {
    if (!hasValidToken()) {
      yield put(authActions.initializeAuthFailure())
      return
    }

    const session = getStoredSession()
    if (!session || !session.user) {
      yield call(clearAuthStorage)
      yield put(authActions.initializeAuthFailure())
      return
    }

    yield put(authActions.initializeAuthSuccess({
      user: session.user,
      token: session.token,
    }))

    // Pre-set admin context from stored role (instant, no API call needed)
    // If init saga fails, the tenant selector is already visible with cached tenant data
    if (session.user.role?.type === 'super_admin' || session.user.role?.type === 'admin') {
      yield put(adminContextActions.setIsAdmin(true))
      yield put(adminContextActions.restoreFromStorage())
    }

    yield put(initActions.initialize())
  } catch (error) {
    yield call(clearAuthStorage)
    yield put(authActions.initializeAuthFailure())
  }
}

function getApiErrorMessage(error: unknown, fallback: string): string {
  const axiosError = error as { response?: { data?: unknown }; message?: string };
  const data = axiosError?.response?.data;

  if (typeof data === 'string') return data;
  if (Array.isArray(data)) {
    const firstMessage = data.find((item) => typeof item?.message === 'string')?.message;
    if (firstMessage) return String(firstMessage);
  }
  if (data && typeof data === 'object') {
    const typed = data as { error?: unknown; message?: unknown };
    if (typeof typed.message === 'string') return typed.message;
    if (typeof typed.error === 'string') return typed.error;
    if (typed.error && typeof (typed.error as { message?: unknown }).message === 'string') {
      return String((typed.error as { message?: unknown }).message);
    }
  }

  const issueList =
    (error as { issues?: Array<{ message?: string }> }).issues ??
    (error as { errors?: Array<{ message?: string }> }).errors;
  if (Array.isArray(issueList)) {
    const firstIssue = issueList.find((issue) => typeof issue?.message === 'string');
    if (firstIssue?.message) return firstIssue.message;
  }

  return error instanceof Error ? error.message : fallback;
}

function* persistAuthenticatedSession(user: AuthUser, token?: string) {
  const session = getStoredSession()
  const nextToken = token ?? session?.token

  if (!nextToken) {
    return
  }

  const rememberMe: boolean = yield call(isPersistentSession)
  yield call(storeSession, nextToken, user, rememberMe)
}

function* loginSaga(action: PayloadAction<LoginCredentials>) {
  try {
    const result: SagaReturnType<typeof authApi.login> = yield call(
      [authApi, authApi.login],
      action.payload
    )

    yield call(storeSession, result.token, result.user, action.payload.rememberMe ?? false)

    yield put(authActions.loginSuccess({
      user: result.user,
      token: result.token,
    }))

    yield put(initActions.initialize())
    
    // Show success toast
    showSuccessToast('auth.loginSuccess', 'auth.loginSuccessMessage')
  } catch (error) {
    const axiosError = error as { response?: { status?: number; data?: { error?: { message?: string; details?: { attemptsRemaining?: number } } } } }
    const status = axiosError.response?.status
    const errorData = axiosError.response?.data?.error
    const message = errorData?.message ?? getApiErrorMessage(error, 'auth.errors.loginFailed')

    if (status === 403) {
      yield put(authActions.loginFailure({ message, isAttemptBlocked: true }))
    } else if (status === 400 && errorData?.details?.attemptsRemaining !== undefined) {
      yield put(authActions.loginFailure({ message, attemptsRemaining: errorData.details.attemptsRemaining }))
    } else {
      yield put(authActions.loginFailure({ message }))
    }
    
    showErrorToast('auth.loginError', message)
  }
}

function* registerSaga(action: PayloadAction<RegisterData>) {
  try {
    const result: SagaReturnType<typeof authApi.register> = yield call(
      [authApi, authApi.register],
      action.payload
    )

    yield put(authActions.registerSuccess({
      user: result.user,
      tenant: result.tenant,
      status: result.status,
      message: result.message,
    }))
    
    // Show success toast
    showSuccessToast('auth.registerSuccess', result.message)
  } catch (error) {
    const message = getApiErrorMessage(error, 'auth.errors.registrationFailed')
    yield put(authActions.registerFailure(message))
    
    // Show error toast
    showErrorToast('auth.registerError', message)
  }
}

function* logoutSaga() {
  yield call(clearAuthStorage)
  yield put(adminContextActions.resetAdminContext())
  yield put(initActions.reset())
  yield put(authActions.logoutComplete())
  
  // Show success toast
  showSuccessToast('auth.logoutSuccess', 'auth.logoutSuccessMessage')
}

function* deleteAccountSaga(action: PayloadAction<DeleteAccountRequest>) {
  try {
    const result: SagaReturnType<typeof authApi.deleteAccount> = yield call(
      [authApi, authApi.deleteAccount],
      action.payload
    )

    yield call(clearAuthStorage)
    yield put(adminContextActions.resetAdminContext())
    yield put(initActions.reset())
    yield put(authActions.deleteAccountSuccess())

    showSuccessToast('auth.deleteAccountSuccess', result.message)
  } catch (error) {
    const message = getApiErrorMessage(error, 'auth.errors.deleteAccountFailed')
    yield put(authActions.deleteAccountFailure(message))
    showErrorToast('auth.deleteAccountFailed', message)
  }
}

function* updateProfileSaga(action: PayloadAction<UpdateProfileRequest>) {
  try {
    const result: SagaReturnType<typeof authApi.updateProfile> = yield call(
      [authApi, authApi.updateProfile],
      action.payload
    )

    yield* persistAuthenticatedSession(result.user)

    yield put(authActions.updateProfileSuccess(result.user))
    showSuccessToast('auth.updateProfileSuccess', 'auth.updateProfileSuccessDesc')
  } catch (error) {
    const message = getApiErrorMessage(error, 'auth.errors.updateProfileFailed')
    yield put(authActions.updateProfileFailure(message))
    showErrorToast('auth.updateProfileFailed', message)
  }
}

function* changePasswordSaga(action: PayloadAction<ChangePasswordRequest>) {
  try {
    const result: SagaReturnType<typeof authApi.changePassword> = yield call(
      [authApi, authApi.changePassword],
      action.payload
    )

    yield* persistAuthenticatedSession(result.user, result.jwt)

    yield put(authActions.changePasswordSuccess(result))
    showSuccessToast('auth.changePasswordSuccess', 'auth.changePasswordSuccessMessage')
  } catch (error) {
    const message = getApiErrorMessage(error, 'auth.errors.passwordChangeFailed')
    yield put(authActions.changePasswordFailure(message))
    showErrorToast('auth.changePasswordError', message)
  }
}

function* requestChangePasswordOtpSaga(action: PayloadAction<ChangePasswordOtpRequest>) {
  try {
    const result: SagaReturnType<typeof authApi.requestChangePasswordOtp> = yield call(
      [authApi, authApi.requestChangePasswordOtp],
      action.payload
    )

    yield put(authActions.requestChangePasswordOtpSuccess(result))
    showSuccessToast('auth.changePasswordOtpSent', result.message)
  } catch (error) {
    const message = getApiErrorMessage(error, 'auth.errors.verificationCodeFailed')
    yield put(authActions.requestChangePasswordOtpFailure(message))
    showErrorToast('auth.changePasswordOtpError', message)
  }
}

function* forgotPasswordSaga(action: PayloadAction<ForgotPasswordRequest>) {
  try {
    const result: SagaReturnType<typeof authApi.forgotPassword> = yield call(
      [authApi, authApi.forgotPassword],
      action.payload
    )

    yield put(authActions.forgotPasswordSuccess({
      message: result.message,
    }))
    
    // Show success toast
    showSuccessToast('auth.passwordResetSent', result.message)
  } catch (error) {
    const message = getApiErrorMessage(error, 'auth.errors.passwordResetRequestFailed')
    yield put(authActions.forgotPasswordFailure(message))

    showErrorToast('auth.passwordResetError')
  }
}

function* resetPasswordSaga(action: PayloadAction<ResetPasswordRequest>) {
  try {
    const result: SagaReturnType<typeof authApi.resetPassword> = yield call(
      [authApi, authApi.resetPassword],
      action.payload
    )

    // If user and token returned, store session
    if (result.user && result.jwt) {
      yield call(storeSession, result.jwt, result.user, false)
      yield put(authActions.resetPasswordSuccess({
        user: result.user,
        token: result.jwt,
        message: result.message,
      }))
      yield put(initActions.initialize())
    } else {
      yield put(authActions.resetPasswordSuccess({
        message: result.message,
      }))
    }
    
    // Show success toast
    showSuccessToast('auth.passwordResetSuccess', result.message)
  } catch (error) {
    const message = getApiErrorMessage(error, 'auth.errors.passwordResetFailed')
    yield put(authActions.resetPasswordFailure(message))
    
    // Show error toast
    showErrorToast('auth.passwordResetError', message)
  }
}

/**
 * Portal init calls /api/users/me. If that fails the token is no longer usable,
 * so drop the session instead of leaving the user on a portal that can't load.
 * Opt-in per portal — fork alongside authSaga.
 */
function* handleInitFailure() {
  yield put(authActions.logout())
}

export function* authExpirySaga() {
  yield takeLatest(initActions.initializeFailure.type, handleInitFailure)
}

export function* authSaga() {
  yield takeLatest(authActions.initializeAuth.type, initializeAuthSaga)
  yield takeLatest(authActions.login.type, loginSaga)
  yield takeLatest(authActions.register.type, registerSaga)
  yield takeLatest(authActions.logout.type, logoutSaga)
  yield takeLatest(authActions.deleteAccount.type, deleteAccountSaga)
  yield takeLatest(authActions.updateProfile.type, updateProfileSaga)
  yield takeLatest(authActions.forgotPassword.type, forgotPasswordSaga)
  yield takeLatest(authActions.resetPassword.type, resetPasswordSaga)
  yield takeLatest(authActions.changePassword.type, changePasswordSaga)
  yield takeLatest(authActions.requestChangePasswordOtp.type, requestChangePasswordOtpSaga)
}
